import { useEffect, useState } from 'react';
import { Email } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useGetAdminStaffQuery } from '@/apis/extended/admin/adminWorkspaceApi';
import { useAdminPatchLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import type { LoanApplication } from '@/types/LoanApplicationType';
import { AdminMessageDialog } from './AdminMessageDialog';
import { displayNameOf } from './loanHealth';

export const LOAN_STATUSES = [
  ['draft', 'Draft'],
  ['submitted', 'Submitted'],
  ['in_review', 'In review'],
  ['approved', 'Approved'],
  ['declined', 'Declined'],
  ['withdrawn', 'Withdrawn'],
] as const;

/**
 * Who works the file (loan officer, processor), its status — optionally
 * emailing the borrower about the change — and a message to the borrower.
 */
export function LoanFileAdminControls({
  app,
  compact,
  approvalBlocker,
}: {
  app: LoanApplication;
  compact?: boolean;
  /** Why the file can't be marked approved yet (e.g. no note rate). */
  approvalBlocker?: string;
}) {
  const { data: staff = [] } = useGetAdminStaffQuery();
  const [patch, { isLoading: saving }] = useAdminPatchLoanApplicationMutation();
  const [status, setStatus] = useState(app.status);
  // Opt in: an email per status change is easy to send by accident.
  const [notify, setNotify] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);

  useEffect(() => {
    setStatus(app.status);
    setNotify(false);
  }, [app._id, app.status]);

  const blocked = status === 'approved' && Boolean(approvalBlocker);

  const assign = async (
    field: 'assignedToAdminId' | 'processorAdminId',
    value: string
  ) => {
    try {
      await patch({ id: app._id, [field]: value || null }).unwrap();
      toast.success(value ? 'Assigned' : 'Unassigned');
    } catch {
      toast.error('Could not assign');
    }
  };

  const saveStatus = async () => {
    if (blocked) return;
    try {
      await patch({ id: app._id, status, notifyBorrower: notify }).unwrap();
      toast.success(
        notify ? 'Status updated · borrower emailed' : 'Status updated'
      );
    } catch {
      toast.error('Update failed');
    }
  };

  const applicant = app.applicantUser;
  const borrower =
    app.borrowers?.find((b) => b.role === 'primary') ?? app.borrowers?.[0];
  const email = applicant?.email || borrower?.email || undefined;
  const firstName =
    borrower?.firstName?.trim() || displayNameOf(applicant).split(' ')[0];
  const staffOptions = [
    <MenuItem key="none" value="">
      Unassigned
    </MenuItem>,
    ...staff.map((s) => (
      <MenuItem key={s._id} value={s._id}>
        {s.name}
      </MenuItem>
    )),
  ];

  return (
    <Stack
      direction={{ xs: 'column', md: compact ? 'row' : 'column' }}
      spacing={1.5}
      sx={{ width: compact ? undefined : '100%' }}
      flexWrap="wrap"
      useFlexGap
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          select
          size="small"
          label="Loan officer"
          value={app.assignedToAdminId ?? ''}
          onChange={(e) => void assign('assignedToAdminId', e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {staffOptions}
        </TextField>
        <TextField
          select
          size="small"
          label="Processor"
          value={app.processorAdminId ?? ''}
          onChange={(e) => void assign('processorAdminId', e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {staffOptions}
        </TextField>
      </Stack>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as LoanApplication['status'])
          }
          sx={{ minWidth: 150 }}
        >
          {LOAN_STATUSES.map(([v, l]) => (
            <MenuItem key={v} value={v}>
              {l}
            </MenuItem>
          ))}
        </TextField>
        {status !== app.status && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
            }
            label="Email the borrower"
          />
        )}
        <Button
          variant="outlined"
          onClick={() => void saveStatus()}
          disabled={saving || status === app.status || blocked}
        >
          Update status
        </Button>
        {blocked ? (
          <Typography variant="caption" color="error">
            {approvalBlocker}
          </Typography>
        ) : null}
        <Button
          startIcon={<Email />}
          onClick={() => setMessageOpen(true)}
          disabled={!email}
        >
          Message borrower
        </Button>
      </Stack>
      <AdminMessageDialog
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        userId={app.applicantUserId}
        firstName={firstName}
        email={email}
        loanFiles={[
          {
            _id: app._id,
            address: app.property?.subjectPropertyAddress,
            flowProfile: app.flowProfile,
            insights: app.adminInsights,
          },
        ]}
        applicationId={app._id}
      />
    </Stack>
  );
}
